import { h, clear, toast, formatDate, showModal, confirmModal, fold } from '../utils/dom.js';
import { iconSvg } from '../utils/icons.js';
import { Eye, UserCheck, XCircle } from 'lucide';
import { RegistrationRequestAPI } from '../services/endpoints.js';
import { Avatar, AVATAR_COLORS } from '../components/avatar.js';
import { Tooltip } from '../components/tooltip.js';
import { PageTabs } from '../components/ui.js';
import { SkeletonTable } from '../components/skeleton.js';

function hashCode(s) { let hash = 0; for (let i = 0; i < s.length; i++) { const ch = s.charCodeAt(i); hash = ((hash << 5) - hash) + ch; hash |= 0; } return Math.abs(hash); }

const REG_STATUS_LABELS = {
  pending: 'Pendiente',
  approved: 'Aprobado',
  rejected: 'Rechazado',
};

export const RegistrationsPage = async (user) => {
  const view = document.getElementById('view');
  clear(view);
  view.appendChild(h('div', { class: 'page-title' },
    h('h1', {}, 'Solicitudes de registro'),
  ));
  let statusFilter = '';

  const filter = h('div', { class: 'filter-bar mb-16' },
    h('input', { type: 'search', name: 'q', 'aria-label': 'Buscar solicitudes de registro', placeholder: 'Buscar por nombre o correo…', style: { minWidth: '220px' }, oninput: applyFilters }),
    h('span', { class: 'filter-label' }, 'Estado'),
    PageTabs({
      tabs: [
        { id: '', label: 'Todas' },
        { id: 'pending', label: 'Pendiente' },
        { id: 'approved', label: 'Aprobado' },
        { id: 'rejected', label: 'Rechazado' },
      ],
      activeTab: statusFilter,
      onTabChange: (id) => { statusFilter = id; applyFilters(); },
    }),
  );
  view.appendChild(filter);
  const card = h('div', { class: 'card' });
  view.appendChild(card);

  const skeleton = h('div', { class: 'table-wrap' }, h('table', { class: 'table' },
    h('thead', {}, h('tr', {},
      h('th', {}, 'Fecha'),
      h('th', {}, 'Nombre'),
      h('th', {}, 'Correo electrónico'),
      h('th', {}, 'Sucursal'),
      h('th', {}, 'Estado'),
      h('th', {}, 'Acciones'),
    )),
    SkeletonTable(5, 6),
  ));
  card.appendChild(skeleton);

  let allRegistrations = [];

  await refresh();

  async function refresh() {
    try {
      const res = await RegistrationRequestAPI.list();
      if (!res.data) return;
      allRegistrations = res.data || [];
      skeleton.remove();
      applyFilters();
    } catch (err) { toast(err.message, 'error'); skeleton.remove(); }
  }

  function applyFilters() {
    const q = filter.querySelector('[name="q"]').value.toLowerCase().trim();
    const foldedQ = fold(q);
    let items = allRegistrations;
    if (q) items = items.filter((r) =>
      fold(r.full_name + ' ' + r.email).includes(foldedQ)
    );
    if (statusFilter) items = items.filter((r) => r.status === statusFilter);
    render(card, items);
  }

  function render(parent, items) {
    clear(parent);
    if (!items.length) { parent.appendChild(h('div', { class: 'table-empty' }, 'No hay solicitudes de registro que coincidan con los filtros.')); return; }
    parent.appendChild(h('div', { class: 'table-wrap' }, h('table', { class: 'table' },
      h('thead', {}, h('tr', {},
        h('th', {}, 'Fecha'),
        h('th', {}, 'Nombre'),
        h('th', {}, 'Correo electrónico'),
        h('th', {}, 'Sucursal'),
        h('th', {}, 'Estado'),
        h('th', {}, 'Acciones'),
      )),
      h('tbody', {}, ...items.map((r) => {
        const avatarColor = AVATAR_COLORS[hashCode(r.full_name) % AVATAR_COLORS.length];
        return h('tr', {},
          h('td', {}, formatDate(r.created_at)),
          h('td', {}, h('div', { class: 'cell-with-avatar' },
            Avatar(r.full_name, avatarColor, 28),
            h('span', {}, r.full_name),
          )),
          h('td', {}, r.email),
          h('td', {}, r.branch_name),
          h('td', {}, h('span', { class: `badge ${r.status}` }, REG_STATUS_LABELS[r.status] || r.status)),
          h('td', {}, h('div', { class: 'actions-cell' },
            Tooltip('Ver detalle', h('button', { class: 'btn-icon-only btn-view', onclick: () => openDetail(r) }, h('span', { html: iconSvg(Eye, 14) }))),
          )),
        );
      })))
    ));
  }

  async function openDetail(r) {
    const content = h('div', {},
      h('h3', {}, `Solicitud de ${r.full_name}`),
      h('div', { class: 'grid grid-2 mt-8' },
        detailField('Estado', r.status === 'pending' ? 'Pendiente' : r.status === 'approved' ? 'Aprobado' : 'Rechazado', `badge ${r.status}`),
        detailField('Fecha', formatDate(r.created_at)),
        detailField('Nombre completo', r.full_name),
        detailField('Correo electrónico', r.email),
        detailField('Número de documento', r.document_number || '—'),
        detailField('Teléfono', r.phone || '—'),
        detailField('Sucursal', r.branch_name),
      ),
      r.status !== 'pending' ? h('div', { class: 'grid grid-2 mt-8' },
        detailField('Revisado por', r.reviewed_by ? `#${r.reviewed_by}` : '—'),
        detailField('Fecha de revisión', r.reviewed_at ? formatDate(r.reviewed_at) : '—'),
        detailField('Notas de revisión', r.review_notes || '—'),
      ) : null,
    );

    if (r.status === 'pending') {
      const approveSection = h('div', { class: 'card mt-16' },
        h('h4', {}, 'Aprobar solicitud'),
        h('label', { class: 'mt-8' }, 'Notas de revisión (opcional)'),
        h('textarea', { name: 'review_notes' }),
        h('div', { class: 'btn-row mt-8' },
          h('button', { class: 'btn btn-success', onclick: submitApprove }, h('span', { html: iconSvg(UserCheck, 16) }), 'Aprobar y crear usuario'),
        ),
      );
      content.appendChild(approveSection);

      const rejectSection = h('div', { class: 'card mt-8' },
        h('h4', {}, 'Rechazar solicitud'),
        h('label', {}, 'Motivo del rechazo (opcional)'),
        h('textarea', { name: 'reject_reason' }),
        h('div', { class: 'btn-row mt-8' },
          h('button', { class: 'btn btn-danger', onclick: submitReject }, h('span', { html: iconSvg(XCircle, 16) }), 'Rechazar'),
        ),
      );
      content.appendChild(rejectSection);
    }

    const m = showModal(content);

    async function submitApprove() {
      const confirmed = await confirmModal('¿Aprobar esta solicitud y crear el usuario?', { confirmText: 'Aprobar', variant: 'success' });
      if (!confirmed) return;
      const review_notes = content.querySelector('[name="review_notes"]').value.trim() || undefined;
      try {
        const res = await RegistrationRequestAPI.approve(r.id, { review_notes });
        const temp = res.data?.temporaryPassword;
        m.close();
        const pwdContent = h('div', {},
          h('h3', {}, 'Solicitud aprobada'),
          h('p', {}, 'Se ha creado el usuario. Compartí esta contraseña temporal de forma segura:'),
          h('div', { class: 'card', style: { background: 'var(--c-panel-2)', textAlign: 'center', fontFamily: 'monospace', fontSize: '16px', letterSpacing: '1px' } },
            h('strong', {}, temp),
          ),
          h('p', { class: 'muted text-sm mt-8' }, 'Se le pedirá al usuario cambiarla en su próximo inicio de sesión.'),
          h('div', { class: 'btn-row mt-16' },
            h('button', { class: 'btn btn-ghost', onclick: () => pwdModal.close() }, 'Cerrar'),
          ),
        );
        const pwdModal = showModal(pwdContent);
        refresh();
      } catch (err) { toast(err.message, 'error'); }
    }

    async function submitReject() {
      const confirmed = await confirmModal('¿Rechazar esta solicitud?', { confirmText: 'Rechazar' });
      if (!confirmed) return;
      const reason = content.querySelector('[name="reject_reason"]').value.trim() || undefined;
      try {
        await RegistrationRequestAPI.reject(r.id, reason);
        toast('Rechazado', 'success');
        m.close();
        refresh();
      } catch (err) { toast(err.message, 'error'); }
    }
  }
};

const detailField = (label, value, badgeClass) => h('div', {},
  h('div', { class: 'muted text-sm' }, label),
  badgeClass ? h('span', { class: badgeClass }, value) : h('div', {}, value),
);
